import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI, employeesAPI, tasksAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Projects = () => {
  const { can, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projectTasks, setProjectTasks] = useState({});
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card');
  const [projectTab, setProjectTab] = useState('active'); // 'active' | 'completed'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('All Priority');
  
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [allocationWarning, setAllocationWarning] = useState(null);
  const [projectDateError, setProjectDateError] = useState('');
  const [taskDateError, setTaskDateError] = useState('');
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  
  const [projectFormData, setProjectFormData] = useState({
    project_name: '',
    description: '',
    customer_name: '',
    manager_id: '',
    planned_start_date: '',
    planned_end_date: '',
    actual_start_date: '',
    actual_end_date: '',
    status: 'Active',
    priority: 'Medium',
    rag_override: false,
    rag_status: 'Green',
    rag_override_note: '',
  });

  const [taskFormData, setTaskFormData] = useState({
    task_name: '',
    description: '',
    assigned_to: '',
    planned_start: '',
    planned_end: '',
    actual_start: '',
    actual_end: '',
    forecast_start: '',
    forecast_end: '',
    effort_days: '',
    progress: 0,
    status_id: 6,
    priority: 'Medium',
    allocation_percent: 100
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, employeesRes] = await Promise.all([
        projectsAPI.getAll(),
        employeesAPI.getAll()
      ]);
      setProjects(projectsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Error loading data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectTasks = async (projectId) => {
    try {
      const res = await tasksAPI.getAllByProject(projectId);
      setProjectTasks(prev => ({ ...prev, [projectId]: res.data }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const toggleProject = async (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (expandedProjects.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
      if (!projectTasks[projectId]) {
        await fetchProjectTasks(projectId);
      }
    }
    setExpandedProjects(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const dateOnly = dateString.substring(0, 10);
    const [year, month, day] = dateOnly.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateLong = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const convertDateForDB = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts;
    return `${year}-${month}-${day}`;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const RAG_CONFIG = {
    Red:   { color: '#e74c3c', bg: '#fdf2f2', label: 'Red',   title: 'Project overdue' },
    Amber: { color: '#f39c12', bg: '#fef9ec', label: 'Amber', title: 'Task(s) overdue' },
    Green: { color: '#27ae60', bg: '#f0faf4', label: 'Green', title: 'On track' },
  };

  const RagDot = ({ rag, isOverride, small }) => {
    const cfg = RAG_CONFIG[rag] || RAG_CONFIG.Green;
    const size = small ? 10 : 12;
    return (
      <span
        title={`${cfg.title}${isOverride ? ' (manual override)' : ''}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          padding: small ? '2px 7px' : '3px 9px',
          borderRadius: '20px',
          background: cfg.bg,
          border: `1px solid ${cfg.color}`,
          fontSize: small ? '11px' : '12px',
          fontWeight: '600',
          color: cfg.color,
          whiteSpace: 'nowrap',
          cursor: 'default',
        }}
      >
        <span style={{ width: size, height: size, borderRadius: '50%', background: cfg.color, display: 'inline-block', flexShrink: 0 }} />
        {cfg.label}
        {isOverride && <span style={{ fontSize: '10px', opacity: 0.7 }}>✎</span>}
      </span>
    );
  };

  // Active tab: Active + On Hold. Completed tab: Completed + Cancelled.
  const ACTIVE_STATUSES    = ['Active', 'On Hold'];
  const COMPLETED_STATUSES = ['Completed', 'Cancelled'];
  const tabStatuses = projectTab === 'active' ? ACTIVE_STATUSES : COMPLETED_STATUSES;

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (project.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab      = tabStatuses.includes(project.status);
    const matchesPriority = filterPriority === 'All Priority' || project.priority === filterPriority;
    return matchesSearch && matchesTab && matchesPriority;
  });

  const activeCount    = projects.filter(p => ACTIVE_STATUSES.includes(p.status)).length;
  const completedCount = projects.filter(p => COMPLETED_STATUSES.includes(p.status)).length;

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    setProjectDateError('');
    const plannedStart = convertDateForDB(projectFormData.planned_start_date);
    const plannedEnd = convertDateForDB(projectFormData.planned_end_date);
    const actualStart = convertDateForDB(projectFormData.actual_start_date);
    const actualEnd = convertDateForDB(projectFormData.actual_end_date);

    if (plannedStart && plannedEnd && plannedEnd < plannedStart) {
      setProjectDateError('Planned end date cannot be earlier than planned start date.');
      return;
    }
    if (actualStart && actualEnd && actualEnd < actualStart) {
      setProjectDateError('Actual end date cannot be earlier than actual start date.');
      return;
    }
    try {
      const dataToSend = {
        ...projectFormData,
        planned_start_date: plannedStart,
        planned_end_date: plannedEnd,
        actual_start_date: actualStart,
        actual_end_date: actualEnd,
      };
      if (editingProjectId) {
        await projectsAPI.update(editingProjectId, { ...dataToSend, changed_by_id: user?.user_id, changed_by_name: user?.name });
      } else {
        await projectsAPI.create(dataToSend);
      }
      setShowProjectModal(false);
      resetProjectForm();
      fetchData();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project');
    }
  };

  const handleProjectEdit = (project) => {
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      const dateOnly = dateString.substring(0, 10);
      const [year, month, day] = dateOnly.split('-');
      return `${day}/${month}/${year}`;
    };
    setProjectFormData({
      project_name: project.project_name,
      description: project.description || '',
      customer_name: project.customer_name || '',
      manager_id: project.manager_id || '',
      planned_start_date: formatDateForInput(project.planned_start_date),
      planned_end_date: formatDateForInput(project.planned_end_date),
      actual_start_date: formatDateForInput(project.actual_start_date),
      actual_end_date: formatDateForInput(project.actual_end_date),
      status: project.status,
      priority: project.priority || 'Medium',
      rag_override: !!project.rag_override,
      rag_status: project.rag_status || project.rag || 'Green',
      rag_override_note: project.rag_override_note || '',
    });
    setEditingProjectId(project.project_id);
    setShowProjectModal(true);
  };

  const handleProjectDelete = async (id, name) => {
    if (window.confirm(`Delete "${name}"?`)) {
      try {
        await projectsAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Error deleting project');
      }
    }
  };

  const resetProjectForm = () => {
    setProjectFormData({
      project_name: '',
      description: '',
      customer_name: '',
      manager_id: '',
      planned_start_date: '',
      planned_end_date: '',
      actual_start_date: '',
      actual_end_date: '',
      status: 'Active',
      priority: 'Medium',
      rag_override: false,
      rag_status: 'Green',
      rag_override_note: '',
    });
    setEditingProjectId(null);
    setProjectDateError('');
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    setTaskDateError('');
    const { planned_start, planned_end, actual_start, actual_end } = taskFormData;

    if (planned_start && planned_end && planned_end < planned_start) {
      setTaskDateError('Planned end date cannot be earlier than planned start date.');
      return;
    }
    if (actual_start && actual_end && actual_end < actual_start) {
      setTaskDateError('Actual end date cannot be earlier than actual start date.');
      return;
    }
    if (Number(taskFormData.status_id) === 5 && (!actual_start || !actual_end)) {
      setTaskDateError('Actual start and actual end dates are required to mark a task as Completed.');
      return;
    }
    try {
      if (editingTaskId) {
        await tasksAPI.update(editingTaskId, { ...taskFormData, changed_by_id: user?.user_id, changed_by_name: user?.name });
      } else {
        await tasksAPI.create(currentProjectId, taskFormData);
      }
      setShowTaskModal(false);
      resetTaskForm();
      await fetchProjectTasks(currentProjectId);
      fetchData();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Error saving task');
    }
  };

  const handleAddTask = (projectId) => {
    resetTaskForm();
    setCurrentProjectId(projectId);
    setShowTaskModal(true);
  };

  const handleTaskEdit = (task, projectId) => {
    setCurrentProjectId(projectId);
    const formData = {
      task_name: task.task_name,
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      planned_start: task.planned_start ? task.planned_start.substring(0, 10) : '',
      planned_end: task.planned_end ? task.planned_end.substring(0, 10) : '',
      actual_start: task.actual_start ? task.actual_start.substring(0, 10) : '',
      actual_end: task.actual_end ? task.actual_end.substring(0, 10) : '',
      forecast_start: task.forecast_start ? task.forecast_start.substring(0, 10) : '',
      forecast_end: task.forecast_end ? task.forecast_end.substring(0, 10) : '',
      effort_days: task.effort_days || '',
      progress: task.progress || 0,
      status_id: task.status_id || 6,
      priority: task.priority || 'Medium',
      allocation_percent: task.allocation_percent || 100
    };
    setTaskFormData(formData);
    setEditingTaskId(task.task_id);
    setShowTaskModal(true);
    // Pass task.task_id directly since setEditingTaskId is async
    setTimeout(() => checkAllocationWithExclude(formData, task.task_id), 0);
  };

  const handleTaskDelete = async (taskId, taskName, projectId) => {
    if (window.confirm(`Delete task "${taskName}"?`)) {
      try {
        await tasksAPI.delete(taskId, { changed_by_id: user?.user_id, changed_by_name: user?.name });
        await fetchProjectTasks(projectId);
        fetchData();
      } catch (error) {
        console.error('Error deleting task:', error);
        alert(error.response?.data?.error || 'Error deleting task');
      }
    }
  };

  const resetTaskForm = () => {
    setTaskFormData({
      task_name: '',
      description: '',
      assigned_to: '',
      planned_start: '',
      planned_end: '',
      actual_start: '',
      actual_end: '',
      forecast_start: '',
      forecast_end: '',
      effort_days: '',
      progress: 0,
      status_id: 6,
      priority: 'Medium',
      allocation_percent: 100
    });
    setEditingTaskId(null);
    setCurrentProjectId(null);
    setAllocationWarning(null);
    setTaskDateError('');
  };

  const handleProjectChange = (e) => {
    setProjectFormData({ ...projectFormData, [e.target.name]: e.target.value });
  };

  const checkAllocationWithExclude = async (formData, excludeId) => {
    const { assigned_to, planned_start, planned_end, allocation_percent } = formData;
    if (!assigned_to || !planned_start || !planned_end || allocation_percent === '' || allocation_percent === null || allocation_percent === undefined) {
      setAllocationWarning(null);
      return;
    }
    try {
      const res = await employeesAPI.checkAllocation(assigned_to, {
        planned_start,
        planned_end,
        allocation_percent,
        exclude_task_id: excludeId || 0
      });
      if (res.data.is_overallocated) {
        setAllocationWarning(res.data);
      } else {
        setAllocationWarning(null);
      }
    } catch (error) {
      setAllocationWarning(null);
    }
  };

  const checkAllocation = async (formData) => {
    checkAllocationWithExclude(formData, editingTaskId || 0);
  };

  const handleTaskChange = (e) => {
    const updated = { ...taskFormData, [e.target.name]: e.target.value };
    setTaskFormData(updated);
    checkAllocation(updated);
  };

  if (loading) {
    return <div className="loading">Loading projects...</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title">📂 Projects</h2>
          <p className="page-subtitle">Manage and track all your team projects</p>
        </div>
        {can('createEditProject') && (
        <button className="btn btn-primary" onClick={() => { resetProjectForm(); setShowProjectModal(true); }}>
          ➕ New Project
        </button>
        )}
      </div>

      {/* ── Tab bar: Active / Completed ── */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #e2e8f0', marginBottom: '20px' }}>
        {[
          { key: 'active',    label: 'Active Projects',    count: activeCount,    icon: '📁' },
          { key: 'completed', label: 'Completed / Cancelled', count: completedCount, icon: '✅' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setProjectTab(tab.key); setSearchTerm(''); setFilterPriority('All Priority'); }}
            style={{
              padding: '10px 24px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: projectTab === tab.key ? '700' : '500',
              color: projectTab === tab.key ? '#2563eb' : '#64748b',
              borderBottom: projectTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
              marginBottom: '-2px', display: 'flex', alignItems: 'center', gap: '8px',
              transition: 'color 0.15s',
            }}
          >
            {tab.icon} {tab.label}
            <span style={{
              background: projectTab === tab.key ? '#dbeafe' : '#f1f5f9',
              color: projectTab === tab.key ? '#1d4ed8' : '#94a3b8',
              borderRadius: '99px', padding: '1px 8px', fontSize: '12px', fontWeight: '700',
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ── Filters and View Toggle ── */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            placeholder="🔍 Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '10px 15px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>
        <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} style={{ padding: '10px 15px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
          <option>All Priority</option>
          <option>Low</option>
          <option>Medium</option>
          <option>High</option>
          <option>Critical</option>
        </select>
        <div style={{ display: 'flex', gap: '8px', borderRadius: '8px', border: '1px solid #ddd', padding: '4px', background: '#fff' }}>
          <button onClick={() => setViewMode('card')} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: viewMode === 'card' ? '#3498db' : 'transparent', color: viewMode === 'card' ? '#fff' : '#7f8c8d', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
            📇 Cards
          </button>
          <button onClick={() => setViewMode('table')} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', background: viewMode === 'table' ? '#3498db' : 'transparent', color: viewMode === 'table' ? '#fff' : '#7f8c8d', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
            📊 Table
          </button>
        </div>
      </div>

      {/* Content */}
      {filteredProjects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📁</div>
          <div className="empty-state-text">No projects found</div>
          <button className="btn btn-primary" onClick={() => setShowProjectModal(true)} style={{ marginTop: '20px' }}>
            Create Your First Project
          </button>
        </div>
      ) : viewMode === 'card' ? (
        /* CARD VIEW - Table Structure for Perfect Alignment */
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            {/* Headers */}
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e1e8ed' }}>
                <th style={{ width: '40px', padding: '12px 8px', textAlign: 'left' }}></th>
                <th style={{ width: '250px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Project</th>
                <th style={{ width: '140px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Assigned To</th>
                <th style={{ width: '200px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Planned Dates</th>
                <th style={{ width: '150px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Actual Dates</th>
                <th style={{ width: '80px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>RAG</th>
                <th style={{ width: '90px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Status</th>
                <th style={{ width: '90px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Priority</th>
                <th style={{ width: '100px', padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Progress</th>
                <th style={{ width: '100px', padding: '12px 16px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#7f8c8d', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            
            {/* Project Rows */}
            <tbody>
              {filteredProjects.map((project) => {
                const isExpanded = expandedProjects.has(project.project_id);
                const tasks = projectTasks[project.project_id] || [];
                const progress = project.total_tasks > 0 ? Math.round((project.completed_tasks / project.total_tasks) * 100) : 0;
                
                return (
                  <React.Fragment key={project.project_id}>
                    {/* Project Row */}
                    <tr 
                      style={{ 
                        background: '#fff',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      onClick={() => toggleProject(project.project_id)}
                    >
                      {/* Expand Arrow */}
                      <td style={{ padding: '16px 8px', verticalAlign: 'top' }}>
                        <div style={{ fontSize: '14px', color: '#95a5a6' }}>
                          {isExpanded ? '▼' : '▶'}
                        </div>
                      </td>
                      
                      {/* Project Name */}
                      <td style={{ padding: '16px 16px', verticalAlign: 'top' }}>
                        <Link 
                          to={`/projects/${project.project_id}`} 
                          onClick={(e) => e.stopPropagation()} 
                          style={{ textDecoration: 'none' }}
                        >
                          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>
                            {project.project_name}
                          </h3>
                        </Link>
                        {project.description && (
                          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#95a5a6', lineHeight: '1.3' }}>
                            {project.description.substring(0, 50)}...
                          </p>
                        )}
                      </td>
                      
                      {/* Assigned To */}
                      <td style={{ padding: '16px 16px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ 
                            width: '28px', 
                            height: '28px', 
                            borderRadius: '50%', 
                            background: project.manager_id ? '#3498db' : '#95a5a6', 
                            color: '#fff', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            fontSize: '11px', 
                            fontWeight: '600',
                            flexShrink: 0
                          }}>
                            {getInitials(project.manager_name || 'NA')}
                          </div>
                          <span style={{ fontSize: '13px', color: '#2c3e50' }}>
                            {project.manager_name || 'Not assigned'}
                          </span>
                        </div>
                      </td>
                      
                      {/* Planned Dates */}
                      <td style={{ padding: '16px 16px', fontSize: '13px', color: '#7f8c8d', verticalAlign: 'top' }}>
                        {formatDateLong(project.planned_start_date)} — {formatDateLong(project.planned_end_date)}
                      </td>
                      
                      {/* Actual Dates */}
                      <td style={{ padding: '16px 16px', fontSize: '13px', color: '#7f8c8d', verticalAlign: 'top' }}>
                        {formatDateLong(project.actual_start_date)} {project.actual_end_date ? `— ${formatDateLong(project.actual_end_date)}` : '—'}
                      </td>
                      
                      {/* RAG */}
                      <td style={{ padding: '16px 16px', verticalAlign: 'top' }}>
                        <RagDot rag={project.rag} isOverride={project.rag_is_override} />
                      </td>
                      
                      {/* Status */}
                      <td style={{ padding: '16px 16px', verticalAlign: 'top' }}>
                        <span className={`badge ${
                          project.status === 'Active' ? 'badge-success' :
                          project.status === 'On Hold' ? 'badge-warning' :
                          project.status === 'Completed' ? 'badge-info' :
                          'badge-danger'
                        }`} style={{ fontSize: '12px', padding: '4px 10px', whiteSpace: 'nowrap' }}>
                          {project.status}
                        </span>
                      </td>
                      
                      {/* Priority */}
                      <td style={{ padding: '16px 16px', verticalAlign: 'top' }}>
                        <span className={`badge ${
                          project.priority === 'High' || project.priority === 'Critical' ? 'badge-danger' :
                          project.priority === 'Medium' ? 'badge-warning' :
                          'badge-info'
                        }`} style={{ fontSize: '12px', padding: '4px 10px', whiteSpace: 'nowrap' }}>
                          {project.priority}
                        </span>
                      </td>
                      
                      {/* Progress */}
                      <td style={{ padding: '16px 16px', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div className="progress" style={{ flex: 1, height: '6px' }}>
                            <div className="progress-bar success" style={{ width: `${progress}%` }} />
                          </div>
                          <span style={{ fontSize: '12px', color: '#7f8c8d', minWidth: '30px' }}>
                            {project.completed_tasks || 0}/{project.total_tasks || 0}
                          </span>
                        </div>
                      </td>
                      
                      {/* Actions */}
                      <td style={{ padding: '16px 16px', textAlign: 'right', verticalAlign: 'top' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          {can('createEditProject') && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleProjectEdit(project)}
                            style={{ padding: '6px 10px', fontSize: '14px', minWidth: 'auto' }}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          )}
                          {can('createEditProject') && (
                          <button
                            className="btn btn-sm"
                            onClick={() => handleProjectDelete(project.project_id, project.project_name)}
                            style={{ padding: '6px 10px', fontSize: '14px', minWidth: 'auto', background: '#fff', border: '1px solid #e1e8ed', color: '#e74c3c', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseOver={(e) => e.target.style.background = '#fee'}
                            onMouseOut={(e) => e.target.style.background = '#fff'}
                            title="Delete"
                          >
                            🗑️
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    
                    {/* Tasks Rows (when expanded) */}
                    {isExpanded && tasks.length > 0 && tasks.map((task) => (
                      <tr key={task.task_id} style={{ background: '#f8f9fa' }}>
                        <td></td>
                      <td colSpan="9" style={{ padding: '8px 16px' }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '16px',
                            padding: '8px 12px',
                            background: '#fff',
                            border: '1px solid #e1e8ed',
                            borderRadius: '6px'
                          }}>
                            <span style={{ color: '#95a5a6', fontSize: '12px' }}>└─</span>
                            <span style={{ fontWeight: '500', fontSize: '14px', flex: 1 }}>{task.task_name}</span>
                            <span style={{ fontSize: '13px', color: '#7f8c8d', minWidth: '120px' }}>
                              {task.assigned_to_name || 'Unassigned'}
                            </span>
                            <span style={{ fontSize: '12px', color: '#95a5a6', minWidth: '180px' }}>
                              {formatDateLong(task.planned_start)} — {formatDateLong(task.planned_end)}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '80px' }}>
                              <div className="progress" style={{ flex: 1, height: '4px' }}>
                                <div className="progress-bar success" style={{ width: `${task.progress}%` }} />
                              </div>
                              <span style={{ fontSize: '12px', color: '#7f8c8d' }}>{task.progress}%</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              {can('createEditTask') && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleTaskEdit(task, project.project_id)}
                                style={{ padding: '4px 8px', fontSize: '12px', minWidth: 'auto' }}
                                title="Edit"
                              >
                                ✏️
                              </button>
                              )}
                              {can('createEditTask') && (
                              <button
                                className="btn btn-sm"
                                onClick={() => handleTaskDelete(task.task_id, task.task_name, project.project_id)}
                                style={{ padding: '4px 8px', fontSize: '12px', minWidth: 'auto', background: '#fff', border: '1px solid #e1e8ed', color: '#e74c3c', borderRadius: '6px', cursor: 'pointer' }}
                                title="Delete"
                              >
                                🗑️
                              </button>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Add Task Button (when expanded) */}
                    {isExpanded && can('createEditProject') && (
                      <tr style={{ background: '#f8f9fa' }}>
                        <td></td>
                        <td colSpan="9" style={{ padding: '8px 16px 16px 16px' }}>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleAddTask(project.project_id)}
                            style={{ fontSize: '13px', padding: '8px 16px' }}
                          >
                            ➕ Add Task
                          </button>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* TABLE VIEW */
        <div className="card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}></th>
                  <th>Project / Task</th>
                  <th>Assigned To</th>
                  <th>Planned Dates</th>
                  <th>Actual Dates</th>
                  <th>RAG</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Progress</th>
                  <th style={{ minWidth: '200px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => (
                  <React.Fragment key={project.project_id}>
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: '600' }}>
                      <td>
                        <button onClick={() => toggleProject(project.project_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0' }}>
                          {expandedProjects.has(project.project_id) ? '▼' : '▶'}
                        </button>
                      </td>
                      <td>
                        <Link to={`/projects/${project.project_id}`}><strong>📁 {project.project_name}</strong></Link>
                        {project.description && (<div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px', fontWeight: 'normal' }}>{project.description.substring(0, 60)}...</div>)}
                      </td>
                      <td>{project.manager_name || 'Not assigned'}</td>
                      <td><div style={{ fontSize: '13px' }}>{formatDate(project.planned_start_date)} → {formatDate(project.planned_end_date)}</div></td>
                      <td><div style={{ fontSize: '13px' }}>{formatDate(project.actual_start_date)} → {formatDate(project.actual_end_date)}</div></td>
                      <td><RagDot rag={project.rag} isOverride={project.rag_is_override} small /></td>
                      <td><span className={`badge ${project.status === 'Active' ? 'badge-success' : project.status === 'On Hold' ? 'badge-warning' : 'badge-info'}`}>{project.status}</span></td>
                      <td><span className={`badge ${project.priority === 'High' || project.priority === 'Critical' ? 'badge-danger' : project.priority === 'Medium' ? 'badge-warning' : 'badge-info'}`}>{project.priority}</span></td>
                      <td><div style={{ fontSize: '13px' }}>{project.completed_tasks || 0}/{project.total_tasks || 0}</div></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {can('createEditProject') && <button className="btn btn-sm btn-primary" onClick={() => handleProjectEdit(project)} style={{ marginRight: '8px' }}>✏️ Edit</button>}
                        {can('createEditProject') && <button className="btn btn-sm btn-danger" onClick={() => handleProjectDelete(project.project_id, project.project_name)}>🗑️</button>}
                      </td>
                    </tr>
                    {expandedProjects.has(project.project_id) && (
                      <>
                        {projectTasks[project.project_id]?.map((task) => (
                          <tr key={task.task_id}>
                            <td></td>
                            <td style={{ paddingLeft: '40px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: '#95a5a6' }}>└─</span>
                                <span>{task.task_name}</span>
                              </div>
                            </td>
                            <td>{task.assigned_to_name || 'Unassigned'}</td>
                            <td style={{ fontSize: '12px' }}>{formatDate(task.planned_start)} → {formatDate(task.planned_end)}</td>
                            <td style={{ fontSize: '12px' }}>{formatDate(task.actual_start)} → {formatDate(task.actual_end)}</td>
                            <td><span className="badge badge-info" style={{ fontSize: '11px' }}>{task.status_name}</span></td>
                            <td><span className={`badge ${task.priority === 'High' ? 'badge-danger' : 'badge-warning'}`} style={{ fontSize: '11px' }}>{task.priority}</span></td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div className="progress" style={{ flex: 1, minWidth: '60px', height: '6px' }}>
                                  <div className="progress-bar success" style={{ width: `${task.progress}%` }} />
                                </div>
                                <span style={{ fontSize: '11px' }}>{task.progress}%</span>
                              </div>
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {can('createEditTask') && <button className="btn btn-sm btn-primary" onClick={() => handleTaskEdit(task, project.project_id)} style={{ marginRight: '8px', fontSize: '12px', padding: '4px 8px' }}>✏️</button>}
                              {can('createEditTask') && <button className="btn btn-sm btn-danger" onClick={() => handleTaskDelete(task.task_id, task.task_name, project.project_id)} style={{ fontSize: '12px', padding: '4px 8px' }}>🗑️</button>}
                            </td>
                          </tr>
                        ))}
                        {can('createEditProject') && (
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <td></td>
                          <td colSpan="9" style={{ paddingLeft: '40px' }}>
                            <button className="btn btn-sm btn-secondary" onClick={() => handleAddTask(project.project_id)} style={{ fontSize: '12px' }}>➕ Add Task</button>
                          </td>
                        </tr>
                        )}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Project Modal */}
      {showProjectModal && (
        <div className="modal-overlay" onClick={() => { setShowProjectModal(false); resetProjectForm(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingProjectId ? 'Edit Project' : 'New Project'}</h3>
            </div>
            <form onSubmit={handleProjectSubmit}>
              <div className="form-group">
                <label className="form-label">Project Name *</label>
                <input type="text" name="project_name" className="form-control" value={projectFormData.project_name} onChange={handleProjectChange} required placeholder="Enter project name" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea name="description" className="form-control" value={projectFormData.description} onChange={handleProjectChange} rows="3" placeholder="Project description..." />
              </div>
              <div className="form-group">
                <label className="form-label">Customer Name</label>
                <input type="text" name="customer_name" className="form-control" value={projectFormData.customer_name} onChange={handleProjectChange} placeholder="Customer or client name" />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Manager</label>
                  <select name="manager_id" className="form-control" value={projectFormData.manager_id} onChange={handleProjectChange}>
                    <option value="">Select Manager</option>
                    {employees.map((emp) => (<option key={emp.employee_id} value={emp.employee_id}>{emp.employee_name}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select name="status" className="form-control" value={projectFormData.status} onChange={handleProjectChange} required>
                    <option value="Active">Active</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Planned Start {editingProjectId && '🔒'}</label>
                  <input type="text" name="planned_start_date" className="form-control" value={projectFormData.planned_start_date} onChange={handleProjectChange} disabled={!!editingProjectId} placeholder="DD/MM/YYYY" pattern="\d{2}/\d{2}/\d{4}" style={editingProjectId ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}} />
                  {editingProjectId && (<small style={{ color: '#7f8c8d', fontSize: '11px' }}>Planned dates cannot be changed</small>)}
                </div>
                <div className="form-group">
                  <label className="form-label">Planned End {editingProjectId && '🔒'}</label>
                  <input type="text" name="planned_end_date" className="form-control" value={projectFormData.planned_end_date} onChange={handleProjectChange} disabled={!!editingProjectId} placeholder="DD/MM/YYYY" pattern="\d{2}/\d{2}/\d{4}" style={editingProjectId ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}} />
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Actual Start</label>
                  <input type="text" name="actual_start_date" className="form-control" value={projectFormData.actual_start_date} onChange={handleProjectChange} placeholder="DD/MM/YYYY" pattern="\d{2}/\d{2}/\d{4}" />
                </div>
                <div className="form-group">
                  <label className="form-label">Actual End</label>
                  <input type="text" name="actual_end_date" className="form-control" value={projectFormData.actual_end_date} onChange={handleProjectChange} placeholder="DD/MM/YYYY" pattern="\d{2}/\d{2}/\d{4}" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select name="priority" className="form-control" value={projectFormData.priority} onChange={handleProjectChange}>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              {/* RAG Status */}
              <div style={{ margin: '4px 0 20px 0', padding: '16px', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e1e8ed' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px', margin: 0 }}>
                    🚦 RAG Status
                  </label>
                  {editingProjectId && (
                    <span style={{ fontSize: '12px', color: '#7f8c8d' }}>
                      Auto: <RagDot rag={projects.find(p => p.project_id === editingProjectId)?.rag || 'Green'} isOverride={false} small />
                    </span>
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#2c3e50', marginBottom: projectFormData.rag_override ? '12px' : '0' }}>
                  <input
                    type="checkbox"
                    checked={!!projectFormData.rag_override}
                    onChange={(e) => setProjectFormData(prev => ({ ...prev, rag_override: e.target.checked }))}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  Override RAG manually
                </label>
                {projectFormData.rag_override && (
                  <div style={{ paddingLeft: '24px' }}>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      {['Green', 'Amber', 'Red'].map(val => {
                        const cfg = { Green: { color: '#27ae60', bg: '#f0faf4' }, Amber: { color: '#f39c12', bg: '#fef9ec' }, Red: { color: '#e74c3c', bg: '#fdf2f2' } }[val];
                        const selected = projectFormData.rag_status === val;
                        return (
                          <label key={val} style={{
                            display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                            padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                            border: `2px solid ${selected ? cfg.color : '#ddd'}`,
                            background: selected ? cfg.bg : '#fff',
                            color: selected ? cfg.color : '#7f8c8d',
                            transition: 'all 0.15s'
                          }}>
                            <input
                              type="radio"
                              name="rag_status"
                              value={val}
                              checked={selected}
                              onChange={() => setProjectFormData(prev => ({ ...prev, rag_status: val }))}
                              style={{ display: 'none' }}
                            />
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
                            {val}
                          </label>
                        );
                      })}
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Reason for override (optional)"
                      value={projectFormData.rag_override_note}
                      onChange={(e) => setProjectFormData(prev => ({ ...prev, rag_override_note: e.target.value }))}
                      style={{ fontSize: '13px' }}
                    />
                  </div>
                )}
              </div>

              {projectDateError && (
                <div style={{ margin: '0 0 16px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', borderLeft: '4px solid #e74c3c', color: '#991b1b', fontSize: '14px', fontWeight: '500' }}>
                  ❌ {projectDateError}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowProjectModal(false); resetProjectForm(); }}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingProjectId ? '💾 Update' : '➕ Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => { setShowTaskModal(false); resetTaskForm(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingTaskId ? 'Edit Task' : 'New Task'}</h3>
            </div>
            <form onSubmit={handleTaskSubmit}>
              <div className="form-group">
                <label className="form-label">Task Name *</label>
                <input type="text" name="task_name" className="form-control" value={taskFormData.task_name} onChange={handleTaskChange} required placeholder="Enter task name" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea name="description" className="form-control" value={taskFormData.description} onChange={handleTaskChange} rows="3" placeholder="Task description..." />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Assigned To</label>
                  <select name="assigned_to" className="form-control" value={taskFormData.assigned_to} onChange={handleTaskChange}>
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (<option key={emp.employee_id} value={emp.employee_id}>{emp.employee_name}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select name="priority" className="form-control" value={taskFormData.priority} onChange={handleTaskChange}>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Planned Start {editingTaskId && '🔒'}</label>
                  <input 
                    type="date" 
                    name="planned_start" 
                    className="form-control" 
                    value={taskFormData.planned_start} 
                    onChange={handleTaskChange} 
                    disabled={!!editingTaskId}
                    style={editingTaskId ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  />
                  {editingTaskId && <small style={{ color: '#7f8c8d', fontSize: '11px' }}>Planned dates cannot be changed after creation</small>}
                </div>
                <div className="form-group">
                  <label className="form-label">Planned End {editingTaskId && '🔒'}</label>
                  <input 
                    type="date" 
                    name="planned_end" 
                    className="form-control" 
                    value={taskFormData.planned_end} 
                    onChange={handleTaskChange} 
                    disabled={!!editingTaskId}
                    style={editingTaskId ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label" style={{ color: Number(taskFormData.status_id) === 5 && !taskFormData.actual_start ? '#e74c3c' : undefined }}>
                    Actual Start {Number(taskFormData.status_id) === 5 && <span style={{ color: '#e74c3c' }}>*</span>}
                  </label>
                  <input type="date" name="actual_start" className="form-control" value={taskFormData.actual_start} onChange={handleTaskChange} style={Number(taskFormData.status_id) === 5 && !taskFormData.actual_start ? { borderColor: '#e74c3c', background: '#fef2f2' } : undefined} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ color: Number(taskFormData.status_id) === 5 && !taskFormData.actual_end ? '#e74c3c' : undefined }}>
                    Actual End {Number(taskFormData.status_id) === 5 && <span style={{ color: '#e74c3c' }}>*</span>}
                  </label>
                  <input type="date" name="actual_end" className="form-control" value={taskFormData.actual_end} onChange={handleTaskChange} style={Number(taskFormData.status_id) === 5 && !taskFormData.actual_end ? { borderColor: '#e74c3c', background: '#fef2f2' } : undefined} />
                </div>
              </div>
              {/* Forecast Start + Forecast End — edit mode only */}
              {editingTaskId && (
                <>
                  <div className="grid grid-2">
                    <div className="form-group">
                      <label className="form-label" style={{ color: '#16a34a' }}>
                        🚀 Forecast Start
                        <span style={{ fontWeight: '400', color: '#14532d', fontSize: '11px', marginLeft: '6px' }}>Start earlier than planned?</span>
                      </label>
                      <input
                        type="date"
                        name="forecast_start"
                        className="form-control"
                        value={taskFormData.forecast_start}
                        onChange={handleTaskChange}
                        style={{ borderColor: '#86efac', background: '#f0fdf4' }}
                      />
                      {taskFormData.forecast_start && taskFormData.planned_start && (
                        <small style={{ color: '#14532d', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                          Planned start {taskFormData.planned_start} · {taskFormData.forecast_start < taskFormData.planned_start
                            ? `starting ${Math.round((new Date(taskFormData.planned_start + 'T12:00:00') - new Date(taskFormData.forecast_start + 'T12:00:00')) / 86400000)} day(s) early`
                            : `starting ${Math.round((new Date(taskFormData.forecast_start + 'T12:00:00') - new Date(taskFormData.planned_start + 'T12:00:00')) / 86400000)} day(s) late`}
                        </small>
                      )}
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                      <small style={{ color: '#14532d', fontSize: '12px', lineHeight: '1.5', background: '#dcfce7', padding: '8px 10px', borderRadius: '6px', border: '1px solid #86efac' }}>
                        📌 Planned Start is locked. Use Forecast Start to make this task available for allocation earlier — overdue is still based on Planned End.
                      </small>
                    </div>
                  </div>
                  <div className="grid grid-2">
                    <div className="form-group">
                      <label className="form-label" style={{ color: '#d97706' }}>
                        🕐 Forecast End
                        <span style={{ fontWeight: '400', color: '#92400e', fontSize: '11px', marginLeft: '6px' }}>Expected completion date</span>
                      </label>
                      <input
                        type="date"
                        name="forecast_end"
                        className="form-control"
                        value={taskFormData.forecast_end}
                        onChange={handleTaskChange}
                        style={{ borderColor: '#f59e0b', background: '#fffbeb' }}
                      />
                      {taskFormData.forecast_end && taskFormData.planned_end && (
                        <small style={{ color: '#78350f', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                          Planned end {taskFormData.planned_end} · forecast {taskFormData.forecast_end > taskFormData.planned_end
                            ? `slips by ${Math.round((new Date(taskFormData.forecast_end + 'T12:00:00') - new Date(taskFormData.planned_end + 'T12:00:00')) / 86400000)} day(s)`
                            : `${Math.round((new Date(taskFormData.planned_end + 'T12:00:00') - new Date(taskFormData.forecast_end + 'T12:00:00')) / 86400000)} day(s) early`}
                        </small>
                      )}
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                      <small style={{ color: '#92400e', fontSize: '12px', lineHeight: '1.5', background: '#fef3c7', padding: '8px 10px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                        📌 Planned End is locked. Forecast End drives the schedule and heatmap — overdue is still based on Planned End.
                      </small>
                    </div>
                  </div>
                </>
              )}
              <div className="form-group">
                <label className="form-label">Allocation %</label>
                <input type="number" name="allocation_percent" className="form-control" value={taskFormData.allocation_percent} onChange={handleTaskChange} min="0" max="100" />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select name="status_id" className="form-control" value={taskFormData.status_id} onChange={handleTaskChange}>
                    <option value="6">Not Started</option>
                    <option value="2">In Progress</option>
                    <option value="5">Completed</option>
                    <option value="3">On Hold</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Progress %</label>
                  <input type="number" name="progress" className="form-control" value={taskFormData.progress} onChange={handleTaskChange} min="0" max="100" />
                </div>
              </div>
              {/* Allocation Warning */}
              {allocationWarning && (
                <div style={{
                  margin: '0 0 16px 0',
                  padding: '14px 16px',
                  background: '#fffbeb',
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  borderLeft: '4px solid #f59e0b'
                }}>
                  <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '6px', fontSize: '14px' }}>
                    ⚠️ Over-allocation Warning — Total: {allocationWarning.total_allocation}%
                  </div>
                  <div style={{ fontSize: '13px', color: '#78350f', marginBottom: '8px' }}>
                    This employee is already allocated {allocationWarning.existing_allocation}% during this period. Adding {taskFormData.allocation_percent}% brings the total to {allocationWarning.total_allocation}%. You can still save.
                  </div>
                  {allocationWarning.conflicting_tasks.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#92400e' }}>
                      <strong>Conflicting tasks:</strong>{' '}
                      {allocationWarning.conflicting_tasks.map((t, i) => (
                        <span key={t.task_id}>
                          {t.task_name} ({t.project_name} — {t.allocation_percent}%)
                          {i < allocationWarning.conflicting_tasks.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {taskDateError && (
                <div style={{ margin: '0 0 16px 0', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', borderLeft: '4px solid #e74c3c', color: '#991b1b', fontSize: '14px', fontWeight: '500' }}>
                  ❌ {taskDateError}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowTaskModal(false); resetTaskForm(); }}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingTaskId ? '💾 Update' : '➕ Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
